import { fetchAssociadoPorId } from '../api/associado/associadoApi';
import { ContratoLista, listarContratos } from '../api/contrato/contratoApi';

/**
 * Os termos de uma adesão em curso — fonte comum de `/termo` e `/assinatura`
 * (at-81, feedback de campo de 03/11/2026).
 *
 * 🔴 **A fonte da verdade é o BANCO, não o Redux.** Até a at-80 a tela de termo
 * guardava UM `contratoId` em estado local e o passava à assinatura por
 * `location.state`. Dois defeitos saíam disso:
 *
 *   1. gerar um segundo termo **descartava** o primeiro da tela, e a assinatura
 *      assinava só o último — o primeiro ficava "Aguardando assinatura" para
 *      sempre;
 *   2. retomar um rascunho na etapa de assinatura chegava SEM `contratoId`, e a
 *      tela caía no caminho legado (PDF público só com o rabisco), deixando o
 *      contrato sem assinar enquanto o consultor achava que tinha assinado.
 *
 * Perguntando ao backend "quais contratos este veículo deste associado tem", as
 * duas telas enxergam a mesma coisa, inclusive depois de F5, logout ou troca de
 * aparelho.
 */

const somenteLetrasEDigitos = (valor?: string | null) =>
  (valor ?? '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

/**
 * O veículo DESTE fluxo entre os carros do associado: casa pela placa e só cai
 * no primeiro quando não há placa para comparar. Pegar `carros[0]` às cegas
 * emitia o contrato para outro carro quando o associado tem mais de um.
 */
export function escolherVeiculoDoFluxo(
  carros: { id: string; placa?: string | null }[],
  placa?: string | null,
): string | null {
  if (!carros.length) return null;
  const alvo = somenteLetrasEDigitos(placa);
  const casado = alvo ? carros.find((c) => somenteLetrasEDigitos(c.placa) === alvo) : undefined;
  return (casado ?? carros[0]).id;
}

/** Busca o associado e devolve o id do veículo do fluxo (ou `null`). Lança em falha de rede. */
export async function resolverVeiculoDoFluxo(
  associadoId: string,
  placa?: string | null,
): Promise<string | null> {
  const associado = await fetchAssociadoPorId(associadoId);
  return escolherVeiculoDoFluxo(associado?.carros ?? [], placa);
}

/** Estados que contam como "termo desta adesão". Cancelado e substituído saíram de cena. */
const ESTADOS_VIGENTES = new Set<ContratoLista['status']>(['EMITIDO', 'ASSINADO']);

/**
 * Contratos vigentes do veículo + associado do fluxo, do mais antigo ao mais novo.
 *
 * ⚠️ Filtra pelos DOIS: numa troca de titular com a mesma placa o carro é
 * atualizado no lugar, e os contratos do titular anterior continuam ligados ao
 * mesmo `carroId` — não são desta adesão.
 */
export async function listarContratosDaAdesao(
  carroId: string,
  associadoId: string,
): Promise<ContratoLista[]> {
  const { contratos } = await listarContratos({
    carroId,
    associadoId,
    status: 'TODOS',
    limite: 100,
  });
  return contratos
    .filter((c) => ESTADOS_VIGENTES.has(c.status))
    .sort((a, b) => a.geradoEm.localeCompare(b.geradoEm));
}

/** Nome do contrato para a tela: o título impresso, ou o código como último recurso. */
export function nomeDoContrato(contrato: Pick<ContratoLista, 'titulo' | 'codigoTipo'>): string {
  return contrato.titulo?.trim() || `Termo ${contrato.codigoTipo ?? ''}`.trim();
}
